import {
  Component,
  OnDestroy,
  OnInit,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  switchMap,
  map,
  mergeMap,
  take,
  finalize,
  catchError,
  filter,
} from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, of, debounceTime } from 'rxjs';
import {
  PlAuthService,
  selectLoadedCurrentUser,
  PLAuthError,
  PLAuthSuccess,
  PlImpersonationService,
  PlAuthContextService,
} from 'pl-core-services';
import { environment } from '@environments/environment';
import { PendoLogger } from '@common/pendo.service';
import { HeapLogger } from '@common/heap.service';
import { AuthSyncService } from '@app/auth/auth-sync.service';
import {
  shouldShowMFANotification,
  markMFANotificationShown,
} from '@app/auth/mfa-notification.utils';
import {
  shouldShowRecoveryCodeNotification,
  markRecoveryCodeNotificationShown,
} from '@app/auth/recovery-code-notification.utils';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { getSecuritySettingsUrl } from '@app/common/constant/security-settings';
import { PlModalService } from '@common/services/pl-modal.service';
import { PLDialogService } from '@common/components/pl-dialog';
import { AccountsService } from './user-management/account-users-service';

declare const gtag: Function;

// Must match the `auth/callback` route in app-routing.module.ts.
const AUTH_CALLBACK_PATH = '/auth/callback';

const ALLOWED_GTM_PAGES = [
  '/registration/signup/individual/freemium',
  '/registration/signup-success',
];

@Component({
  selector: 'pl-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private urlcleaningregex = /\?.*/;

  private authCheckInProgress = false;

  private monitoringInterval: any;

  private readonly RETURN_URL_KEY = 'auth_return_url';

  private log(message: string, data?: any) {
    if (environment.debug) {
      // eslint-disable-next-line no-console
      console.log(`[AppComponent] ${message}`, data || '');
    }
  }

  constructor(
    private store: Store,
    private oidcSecurityService: OidcSecurityService,
    private impersonationService: PlImpersonationService,
    private authContext: PlAuthContextService,
    private router: Router,
    private heapLogger: HeapLogger,
    private pendoLogger: PendoLogger,
    private accountsService: AccountsService,
    private authService: PlAuthService,
    private authSyncService: AuthSyncService,
    private modalService: PlModalService,
    private plDialog: PLDialogService
  ) {}

  ngOnInit(): void {
    const isSiriusEnabled = getIsSiriusEnabled();
    if (isSiriusEnabled) {
      // Check for impersonation request on app load
      const impersonationTargetUserId = sessionStorage.getItem(
        'impersonation_target_user_id'
      );

      if (impersonationTargetUserId) {
        this.log(
          'Impersonation requested for user:',
          impersonationTargetUserId
        );

        // Check real auth state immediately, don't wait for emission
        this.oidcSecurityService
          .checkAuth()
          .pipe(
            take(1),
            switchMap((authState: any) => {
              this.log('Auth state checked:', authState);
              if (authState?.isAuthenticated) {
                this.log('Authenticated, performing token exchange...');
                return this.impersonationService.start(
                  impersonationTargetUserId
                );
              }
              this.log('Not authenticated, triggering authorization...');
              // Store current URL before authorization (fallback)
              this.storeReturnUrlIfNeeded();
              // If not authenticated, trigger auth flow
              this.oidcSecurityService.authorize();
              return of(false);
            }),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: (success: boolean) => {
              if (success) {
                this.log('Token exchange successful, reloading...');
                // Ensure the impersonation target key is removed before reload
                // to prevent infinite reload loops
                sessionStorage.removeItem('impersonation_target_user_id');
                // Reload to apply impersonation
                window.location.href = '/';
              }
            },
            error: (error: any) => {
              this.log('Token exchange failed:', error);
              this.impersonationService.stop();
              // Reload to clear any partial state and reset auth flow
              window.location.href = '/';
            },
          });

        // Return early to avoid subscribing to auth state changes twice
        // The impersonation flow above will trigger a window reload to start fresh
        return;
      }

      // const config = this.oidcSecurityService.getConfiguration();

      // NOTE: If you need to, this is a place where you can
      // invoke `this.oidcSecurityService.getAccessToken()`
      // to get initial token state (e.g., inspecting expiry, etc.)
      //
      // It's also a place where you log `{ type: EventTypes[notification.type],
      // value: notification.value }` to see what events are being fired
      // by subscribing to `this.eventService.registerForEvents()` and piping
      // the results after filtering to their `EventTypes` enum values.

      // Subscribe to auth state changes
      this.oidcSecurityService.isAuthenticated$
        .pipe(
          debounceTime(100), // Chrome issues two events in quick succession, Safari does not...giving buffer
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((authState: any) => {
          // NOTE: Deliberately NOT skipped on the callback route.
          // After the callback route completes the isAuthenticated$ observable emits true
          // And this code will run again to fetch user data and groups,
          // which is necessary for the guards.
          // The skip is handled in performAuthCheck() where we check if we're on the callback route and return early.
          if (authState?.isAuthenticated) {
            // Get both userData and idToken for MFA notification check
            combineLatest([
              this.authContext.userData$.pipe(filter((userData) => !!userData)),
              this.oidcSecurityService.getIdToken(),
            ])
              .pipe(take(1), takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: ([userData, idToken]) => {
                  this.handleAuthenticated({
                    isAuthenticated: true,
                    userData,
                    idToken,
                  });

                  this.oidcSecurityService
                    .getAccessToken()
                    .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                    .subscribe((token) => {
                      if (token) {
                        this.log('Starting initial token monitoring');
                        // NOTE: Below, you'll see I'm monitoring token state actively,
                        // this is especially important because `angular-auth-oidc-client` at v13.1.0
                        // (and beyond) unfortunately DOES NOT have a robust production-ready
                        // working implementation of silent renew.
                        //
                        // At version v13.1.0 their solution for automatic token refresh
                        // is not only knowingly broken (a fix was posted a few versions later)
                        // - but even once they did post a fix, it has issues (including
                        // users claiming critical problems in their most up-to-date versions).
                        // But more worrisome is that this leader in the Angular auth space
                        // omits critical safety features you would expect from a clientside
                        // auth library.
                        //
                        // So I wrote a thin wrapper around their core library (which, as
                        // you can see is minimally configured and only a few dozen lines of
                        // code itself).
                        //
                        // My code extending `angular-auth-oidc-client` goes well beyond
                        // what any available FE client library offers out of the box - I've
                        // added the following production-grade features to ensure our system
                        // is as robust and secure as possible:
                        //
                        // - Cross-tab authentication synchronization using BroadcastChannel API and leader election
                        // - Web Worker-based token monitoring and refresh scheduling with exponential backoff
                        // - Robust error handling including server downtime scenarios and token refresh failures
                        // - Secure state preservation and restoration mechanisms
                        // - Performance optimizations via worker offloading and state debouncing
                        // - Comprehensive logging and debugging infrastructure
                        // - Graceful degradation during service interruptions
                        // - Deep NgRx store integration
                        // - Session continuity management across multiple tabs
                        // - Intelligent retry mechanisms that distinguish between different failure types
                        //
                        // The code I've written around `angular-auth-oidc-client` is intended to
                        // provide functionality not available in the core library (or others like it):
                        //
                        // - Enabling SECURE + seamless single sign-on across browser tabs without
                        //   re-authentication, WITHOUT using localStorage (e.g. Okta FE SDKs use this
                        //   insecure method to accomplish what I'm doing here with SessionStorage +
                        //   BroadcastChannel + Web Workers)
                        // - Maintaining active sessions during server outages or connectivity issues by
                        //   implementing a robust token refresh mechanism that can handle a variety of
                        //   network failure conditions and server-side issues
                        // - Providing consistent authentication state across all tabs, including
                        //   synchronized token refresh (even if tabs are hidden or backgrounded), allowing
                        //   users who are authenticated and have not explicitly closed their tab or the browser
                        //   to avoid unnecessary re-authentication
                        this.authSyncService.startTokenMonitoring(token);
                        this.authSyncService.startSessionPolling();
                      } else {
                        this.log('No token found, skipping token monitoring');
                      }
                    });
                },
                error: (error) => {
                  this.log('Error fetching user data', error);
                },
              });
          } else if (!this.authCheckInProgress) {
            if (this.authSyncService.isRefreshing) {
              this.log('Skipping auth check - refresh in progress');
              return;
            }
            // NOTE: Not authenticated, if we're not in the middle of
            // an existing/running authentication check, start one...
            this.performAuthCheck();
          }
        });

      // Handle navigation events
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const cleanedUrl = event.url.replace(this.urlcleaningregex, '');

          if (ALLOWED_GTM_PAGES.includes(cleanedUrl))
            gtag('config', 'GTM-KH7KQBC', {
              page_path: event.urlAfterRedirects,
            });
        }
      });
    } else {
      this.store
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          selectLoadedCurrentUser,
          switchMap((currentUser) =>
            this.accountsService.fetchAccounts().pipe(
              catchError((_error) =>
                // Return empty array so analytics still get initialized with user data
                of([])
              ),
              mergeMap((accounts: any) => {
                const userAccount = accounts && accounts.length && accounts[0];
                const kangaUser$ = userAccount?.uuid
                  ? this.accountsService
                      .fetchAccountKangaUser(userAccount.uuid, currentUser.uuid)
                      .pipe(
                        catchError((_error) =>
                          // Return null so analytics still get initialized without Kanga data
                          of(null)
                        )
                      )
                  : of(null);
                return kangaUser$.pipe(
                  map((kangaUser: any) => ({
                    currentUser,
                    accounts,
                    kangaUser,
                  }))
                );
              })
            )
          )
        )
        .subscribe(({ currentUser, accounts, kangaUser }) => {
          this.heapLogger.setUser(currentUser);
          this.pendoLogger.setUser(currentUser, accounts, kangaUser);
        });
    }
  }

  ngOnDestroy() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }
  }

  /**
   * Stores the current URL as return URL if one isn't already stored.
   * This is a fallback for cases where authorize() is called outside the guard flow.
   */
  private storeReturnUrlIfNeeded(): void {
    // Only store if not already stored (guard takes precedence)
    const existingUrl = sessionStorage.getItem(this.RETURN_URL_KEY);
    if (existingUrl) {
      this.log('Return URL already stored, skipping', existingUrl);
      return;
    }

    const currentUrl =
      window.location.pathname + window.location.search + window.location.hash;

    // Don't store auth-related URLs
    if (
      currentUrl.startsWith('/auth/callback') ||
      currentUrl.startsWith('/auth/logged-out') ||
      currentUrl === '/'
    ) {
      this.log('Current URL not suitable for return URL, skipping', currentUrl);
      return;
    }

    sessionStorage.setItem(this.RETURN_URL_KEY, currentUrl);
    this.log('Stored return URL as fallback', currentUrl);
  }

  private async performAuthCheck(): Promise<void> {
    if (this.authCheckInProgress) {
      // NOTE: This is a defensive check to ensure we don't
      // trigger race conditions by starting multiple auth checks
      // concurrently.
      return;
    }

    if (window.location.pathname === AUTH_CALLBACK_PATH) {
      // NOTE: If we're on the callback route, we don't want to
      // duplicate the auth check it's already doing, so we'll skip
      // and early return to prevent incongruent state conditions.
      return;
    }

    this.authCheckInProgress = true;

    const authStateReceived = await this.authSyncService.waitForAuthState();

    if (!authStateReceived) {
      this.log(
        'No auth state received from other tabs, proceeding with fresh auth check'
      );
    }

    // CRITICAL: Check if user recently logged out BEFORE calling checkAuth()
    // checkAuth() would silently re-authenticate via Sirius session cookie
    // The flag is only cleared when user explicitly clicks "Log in"
    if (this.authSyncService.isRecentlyLoggedOut()) {
      // Before blocking, check if a valid server session exists.
      // This handles cross-subdomain login: user logs out here, logs in on
      // another subdomain (e.g., room.presence.com), then navigates back.
      const hasValidSession = await this.authSyncService.isSessionValid();
      if (hasValidSession) {
        this.log(
          'Valid server session found despite logout flag - clearing flag and proceeding with checkAuth()'
        );
        this.authSyncService.clearLogoutFlag();
        // Fall through to checkAuth() below
      } else {
        this.log(
          'User recently logged out - blocking checkAuth() to prevent auto-login'
        );
        // Same terminal-state requirement as the anonymous branch below:
        // skipping checkAuth() must not leave authState stuck in 'init'
        // with all requests queued behind it (PRO-2436).
        this.store.dispatch(PLAuthError({ error: null }));
        this.authCheckInProgress = false;
        return;
      }
    }

    this.oidcSecurityService
      .checkAuth()
      .pipe(
        take(1),
        finalize(() => {
          this.authCheckInProgress = false;
        })
      )
      .subscribe({
        next: (authResult) => {
          if (!authResult.isAuthenticated) {
            // Authorize is handled at the auth guard level, so we don't need to do anything here
            // This also allows public routes to be accessible when using sirius auth
            //
            // The auth state must still resolve to a terminal value: requests
            // are queued until authState is 'success' or 'error', so a
            // confirmed-anonymous visitor on a public route (e.g. registration
            // signup) would otherwise hang forever in 'init' (PRO-2436).
            this.store.dispatch(PLAuthError({ error: null }));
          }
        },
        error: (error) => {
          // Handle state mismatch errors that occur when multiple tabs
          // start simultaneously and interfere with each other's OAuth flows
          const errorMsg = error?.message || String(error);
          if (errorMsg.includes('could not find matching config for state')) {
            this.log(
              'OIDC state mismatch (likely from another tab) - checking auth status',
              error
            );
            // Check immediately if broadcast arrived while processing error
            this.oidcSecurityService.isAuthenticated$
              .pipe(take(1), takeUntilDestroyed(this.destroyRef))
              .subscribe((authState) => {
                if (!authState.isAuthenticated) {
                  // Don't auto-authorize if user is on the logged-out page
                  const currentPath = window.location.pathname;
                  if (
                    currentPath.startsWith('/auth/logged-out') ||
                    currentPath === '/auth/logged-out'
                  ) {
                    this.log('On logged-out page, skipping auto-authorize');
                    return;
                  }
                  // Still not authenticated, start OAuth flow
                  this.storeReturnUrlIfNeeded();
                  this.oidcSecurityService.authorize();
                }
              });
          } else {
            this.store.dispatch(PLAuthError({ error }));
          }
        },
      });
  }

  private handleAuthenticated(authResult: {
    isAuthenticated: boolean;
    userData: any;
    idToken?: string;
  }) {
    this.authCheckInProgress = false;

    // CRITICAL: Clear logout flag on successful authentication
    // This ensures a page refresh after login doesn't block checkAuth()
    this.authSyncService.clearLogoutFlag();

    // Check if MFA was just configured during this authentication flow
    // NOTE: The mfa_just_configured claim is only in the ID token, not in userData
    // (userData comes from /userinfo endpoint which doesn't include transient claims)
    this.checkMFASetupNotification(authResult.idToken);
    this.checkRecoveryCodeNotification(authResult.idToken);

    // NOTE: The value here is the user UUID coming from user
    // migrated values into our new auth system from our legacy
    // auth system. This value may change over time in which
    // case this should be adjusted to look at the updated key.
    const userUUID = authResult.userData.auth_db_user_uuid;

    const user = {
      uuid: userUUID,
      username: authResult.userData.auth_db_user_username,
      email: authResult.userData.email,
      display_name: authResult.userData.name,
      first_name: authResult.userData.given_name,
      last_name: authResult.userData.family_name,
      is_active: true,
      is_staff: authResult.userData.role !== 'provider',
    };

    this.oidcSecurityService
      .getAccessToken()
      .pipe(
        take(1),
        switchMap((token) => {
          if (!token) {
            throw new Error('Missing access token');
          }

          // First dispatch with base user data
          this.store.dispatch(
            PLAuthSuccess({
              user: authResult.userData,
              token,
            })
          );

          // Proceed to fetch groups using authService
          return this.authService.getUser().pipe(
            map((result) => ({
              token,
              groups: result.groups || [],
            }))
          );
        })
      )
      .subscribe({
        next: ({ token, groups }) => {
          // Second dispatch after groups are fetched
          this.store.dispatch(
            PLAuthSuccess({
              user: { ...user, groups },
              token,
            })
          );
        },
        error: (error) => {
          this.store.dispatch(
            PLAuthError({
              error: error.message || 'Error during authentication.',
            })
          );
        },
      });

    this.accountsService
      .fetchAccounts()
      .pipe(
        catchError((_error) =>
          // Return empty array so analytics still get initialized with user data
          of([])
        ),
        mergeMap((accounts: any) => {
          const userAccount = accounts && accounts.length && accounts[0];
          const kangaUser$ = userAccount?.uuid
            ? this.accountsService
                .fetchAccountKangaUser(userAccount.uuid, userUUID)
                .pipe(
                  catchError((_error) =>
                    // Return null so analytics still get initialized without Kanga data
                    of(null)
                  )
                )
            : of(null);
          const currentUser = authResult.userData;
          return kangaUser$.pipe(
            map((kangaUser: any) => ({ currentUser, accounts, kangaUser }))
          );
        })
      )
      .subscribe({
        next: ({ accounts, kangaUser }) => {
          this.heapLogger.setUser(authResult.userData);
          this.pendoLogger.setUser(authResult.userData, accounts, kangaUser);
        },
        error: (error) => {
          this.store.dispatch(PLAuthError({ error }));
        },
      });
  }

  /**
   * Check if MFA was just configured during this authentication flow.
   * If so, show a notification to the user (once per ID token lifetime).
   *
   * NOTE: The mfa_just_configured claim is ONLY in the ID token, not in the
   * /userinfo response. We must decode the ID token to check this claim.
   */
  private checkMFASetupNotification(idToken: string | undefined): void {
    const mfaType = shouldShowMFANotification(idToken ?? null);

    if (!mfaType) {
      return;
    }

    markMFANotificationShown();

    // Determine modal content based on MFA type
    let title: string;
    let content: string;

    if (mfaType === 'sms') {
      title = 'SMS MFA enabled';
      content =
        'You are now enrolled in SMS multi-factor authentication. You will receive messages per login attempt. ' +
        'Message and data rates may apply. Reply STOP to opt-out or HELP for help.\n\n' +
        'Your security preferences have been saved and can be changed in your settings at any time.';
    } else {
      // Default for 'totp', 'unknown', or any other type
      title = 'Security preferences saved';
      content =
        'Your security preferences have been saved in your user settings. You can update them anytime.';
    }

    this.plDialog
      .open({
        title,
        content,
        buttonLabel: 'I understand',
      })
      .subscribe();
  }

  private checkRecoveryCodeNotification(idToken: string | undefined): void {
    if (!shouldShowRecoveryCodeNotification(idToken ?? null)) {
      return;
    }

    markRecoveryCodeNotificationShown(idToken ?? null);

    this.plDialog
      .open({
        title: 'Recovery code used',
        content:
          'You logged in with a recovery code. Generate new codes anytime in your Security Settings.',
        buttonLabel: 'Visit Security Settings',
      })
      .subscribe(() => {
        window.location.href = getSecuritySettingsUrl();
      });
  }
}
